import { t } from "@lingui/macro";
import type { ErrorMessage } from "@reactive-resume/utils";
import { deepSearchAndParseDates } from "@reactive-resume/utils";
import _axios from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import { redirect } from "react-router";

import { refreshToken } from "@/client/services/auth";

import { USER_KEY } from "../constants/query-keys";
import { toast } from "../hooks/use-toast";
import { isAiInsufficientPointsError } from "../services/ai/errors";
import { translateError } from "../services/errors/translate-error";
import { queryClient } from "./query-client";
import { getCorrelationId } from "../services/logger/correlation";
import { logger } from "../services/logger/logger";

export const axios = _axios.create({ baseURL: "/api", withCredentials: true });

axios.interceptors.request.use((config) => {
  const correlationId = getCorrelationId();
  config.headers["X-Correlation-ID"] = correlationId;
  const start = Date.now();
  config.metadata = { start };
  logger.info(`${config.method?.toUpperCase()} ${config.url}`, "API");
  return config;
});

axios.interceptors.response.use(
  (response) => {
    const duration = Date.now() - response.config.metadata?.start;
    logger.info(`${response.status} ${response.config.url} (${duration}ms)`, "API", { duration });
    return response;
  },
  (error) => {
    const duration = Date.now() - error.config?.metadata?.start;
    logger.error(`${error.response?.status} ${error.config?.url} (${duration}ms)`, error, "API");
    return Promise.reject(error);
  },
);

// Intercept responses to transform ISO dates to JS date objects
axios.interceptors.response.use(
  (response) => {
    const transformedResponse = deepSearchAndParseDates(response.data, ["createdAt", "updatedAt"]);
    return { ...response, data: transformedResponse };
  },
  (error) => {
    if (isAiInsufficientPointsError(error)) {
      if (typeof error.response?.data.message === "string") {
        error.message = error.response.data.message;
      }
      return Promise.reject(error);
    }

    const message = error.response?.data.message as ErrorMessage;
    const description = translateError(message);

    if (description && !error.config?.skipErrorToast) {
      toast({
        variant: "error",
        title: t`Oops, an error occurred!`,
        description,
      });
    }

    return Promise.reject(new Error(message));
  },
);

// Create another instance to handle failed refresh tokens
// Reference: https://github.com/Flyrell/axios-auth-refresh/issues/191
const axiosForRefresh = _axios.create({ baseURL: "/api", withCredentials: true });

// Interceptor to handle expired access token errors
const handleAuthError = () => refreshToken(axiosForRefresh);

// Interceptor to handle expired refresh token errors
const handleRefreshError = async () => {
  await queryClient.invalidateQueries({ queryKey: USER_KEY });
  redirect("/auth/login");
};

// Intercept responses to check for 401 and 403 errors, refresh token and retry the request
createAuthRefreshInterceptor(axios, handleAuthError, { statusCodes: [401, 403] });
createAuthRefreshInterceptor(axiosForRefresh, handleRefreshError);
